import React from 'react';
import { useTranslation } from 'react-i18next';
import { Gift, Tag, CreditCard, Coins, RotateCcw } from 'lucide-react';
import { cn } from 'lib/utils';
import type { CoinLedgerEntry } from 'modules/coins/api';
import { describeLedgerEntry, type LedgerDisplay } from './billingHelpers';

const ICONS: Record<LedgerDisplay['iconKey'], React.ComponentType<{ className?: string }>> = {
  gift: Gift,
  tag: Tag,
  card: CreditCard,
  coins: Coins,
  refund: RotateCcw,
};

interface LedgerRowProps {
  entry: CoinLedgerEntry;
  /** Show the running balance under the amount (history modal). */
  showBalance?: boolean;
  /** Draw a bottom divider (history modal rows). */
  divided?: boolean;
}

/** One coin-ledger line: a quiet source glyph, what happened and when, then the signed amount. */
export function LedgerRow({ entry, showBalance = false, divided = false }: LedgerRowProps) {
  const { t } = useTranslation();
  const d = describeLedgerEntry(entry, t);
  const Icon = ICONS[d.iconKey];

  return (
    <div className={cn('flex items-center gap-3', divided && 'border-b border-border py-3 last:border-0')}>
      <Icon className="h-4 w-4 flex-none text-muted-foreground" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-medium text-foreground">{d.label}</div>
        {d.sub && <div className="truncate text-[12.5px] text-muted-foreground">{d.sub}</div>}
      </div>
      <div className="text-right">
        <span className={cn('text-sm font-semibold tabular-nums', d.positive ? 'text-success-700 dark:text-success-400' : 'text-foreground')}>
          {d.amountStr}
        </span>
        {showBalance && (
          <div className="mt-0.5 text-[12px] tabular-nums text-muted-foreground">
            {t('balance_short', 'Bal')} {d.balanceStr}
          </div>
        )}
      </div>
    </div>
  );
}

export default LedgerRow;
